import React, { useState, useEffect, useCallback } from 'react'
import { superadminAPI } from '../../services/api'
import { useAlerts } from '../../contexts/AlertContext'
import { refreshSecurityRules } from '../../config/documentSecurity'
import {
  HiOutlineShieldCheck,
  HiOutlineLockClosed,
  HiOutlineArrowPath,
  HiOutlineCheckCircle,
  HiOutlineExclamationTriangle,
} from 'react-icons/hi2'

// Display labels
const ROLE_LABELS = {
  owner: 'Owner',
  admin: 'Admin',
  diamond: 'Diamond',
  gold: 'Gold',
  silver: 'Silver',
}

const RESOURCE_LABELS = {
  client: 'Client',
  case: 'Case',
  session: 'Session',
  document: 'Document',
  document_group: 'Doc Group',
  message: 'Message',
}

const ACTION_LABELS = {
  create: 'Create',
  read: 'Read',
  update: 'Update',
  delete: 'Delete',
  list: 'List',
  assign: 'Assign',
  unassign: 'Unassign',
  manage_roles: 'Manage Roles',
}

const SECURITY_LEVEL_LABELS = {
  red: { label: 'Red', subtitle: 'Confidential', color: 'text-danger', bg: 'bg-danger-soft', border: 'border-danger/30' },
  yellow: { label: 'Yellow', subtitle: 'Restricted', color: 'text-yellow-700', bg: 'bg-yellow-50', border: 'border-yellow-200' },
  green: { label: 'Green', subtitle: 'General', color: 'text-success', bg: 'bg-success-soft', border: 'border-success/30' },
}

// Actions temporarily hidden from the UI
const HIDDEN_ACTIONS = ['assign', 'unassign', 'manage_roles']

const ROLE_COLORS = {
  owner: 'bg-brand-soft text-brand border-brand/30',
  admin: 'bg-red-100 text-red-800 border-danger/30',
  diamond: 'bg-info-soft text-info border-info/30',
  gold: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  silver: 'bg-gray-100 text-gray-800 border-gray-200',
}

const PermissionManagementPage = () => {
  const { showSuccess, showError } = useAlerts()

  // Tab state
  const [activeTab, setActiveTab] = useState('permissions')

  // Permission matrix state
  const [permMatrix, setPermMatrix] = useState(null)
  const [permMeta, setPermMeta] = useState({ roles: [], resources: [], actions: [] })
  const [permDirty, setPermDirty] = useState(false)
  const [permLoading, setPermLoading] = useState(true)
  const [permSaving, setPermSaving] = useState(false)

  // Security rules state
  const [secMatrix, setSecMatrix] = useState(null)
  const [secMeta, setSecMeta] = useState({ roles: [], security_levels: [] })
  const [secDirty, setSecDirty] = useState(false)
  const [secLoading, setSecLoading] = useState(true)
  const [secSaving, setSecSaving] = useState(false)

  // Fetch permission matrix
  const fetchPermissions = useCallback(async () => {
    try {
      setPermLoading(true)
      const response = await superadminAPI.getPermissionMatrix()
      if (response.data?.type === 'success') {
        const data = response.data.data
        setPermMatrix(data.matrix)
        setPermMeta({ roles: data.roles, resources: data.resources, actions: data.actions })
        setPermDirty(false)
      } else {
        showError('Failed to load permission matrix')
      }
    } catch (error) {
      console.error('Error fetching permissions:', error)
      showError('Failed to load permission matrix')
    } finally {
      setPermLoading(false)
    }
  }, [showError])

  // Fetch security rules
  const fetchSecurityRules = useCallback(async () => {
    try {
      setSecLoading(true)
      const response = await superadminAPI.getSecurityRules()
      if (response.data?.type === 'success') {
        const data = response.data.data
        setSecMatrix(data.matrix)
        setSecMeta({ roles: data.roles, security_levels: data.security_levels })
        setSecDirty(false)
      } else {
        showError('Failed to load security rules')
      }
    } catch (error) {
      console.error('Error fetching security rules:', error)
      showError('Failed to load security rules')
    } finally {
      setSecLoading(false)
    }
  }, [showError])

  useEffect(() => {
    fetchPermissions()
    fetchSecurityRules()
  }, [fetchPermissions, fetchSecurityRules])

  // Toggle permission checkbox
  const togglePermission = (role, resource, action) => {
    setPermMatrix(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [resource]: {
          ...prev[role][resource],
          [action]: !prev[role][resource][action],
        },
      },
    }))
    setPermDirty(true)
  }

  // Toggle security rule checkbox
  const toggleSecurityRule = (level, role) => {
    setSecMatrix(prev => ({
      ...prev,
      [level]: {
        ...prev[level],
        [role]: !prev[level][role],
      },
    }))
    setSecDirty(true)
  }

  // Save permission matrix
  const savePermissions = async () => {
    try {
      setPermSaving(true)
      const response = await superadminAPI.updatePermissionMatrix({ matrix: permMatrix })
      if (response.data?.type === 'success') {
        showSuccess('Permission matrix saved successfully')
        setPermDirty(false)
      } else {
        showError(response.data?.message || 'Failed to save permission matrix')
      }
    } catch (error) {
      console.error('Error saving permissions:', error)
      showError('Failed to save permission matrix')
    } finally {
      setPermSaving(false)
    }
  }

  // Save security rules
  const saveSecurityRules = async () => {
    try {
      setSecSaving(true)
      const response = await superadminAPI.updateSecurityRules({ matrix: secMatrix })
      if (response.data?.type === 'success') {
        showSuccess('Security rules saved successfully')
        setSecDirty(false)
        // Refresh the frontend cached security rules
        await refreshSecurityRules()
      } else {
        showError(response.data?.message || 'Failed to save security rules')
      }
    } catch (error) {
      console.error('Error saving security rules:', error)
      showError('Failed to save security rules')
    } finally {
      setSecSaving(false)
    }
  }

  // Reset to defaults (re-fetch from server)
  const resetPermissions = () => {
    fetchPermissions()
  }
  const resetSecurityRules = () => {
    fetchSecurityRules()
  }

  return (
    <div className="p-4 md:p-6 max-w-full">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <HiOutlineShieldCheck className="text-danger" />
          Permission Management
        </h1>
        <p className="text-gray-500 mt-1">
          Manage global role permissions and document security rules. Changes affect all users system-wide.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('permissions')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'permissions'
              ? 'border-red-600 text-danger'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <HiOutlineShieldCheck className="w-4 h-4" />
          Role Permissions
          {permDirty && <span className="w-2 h-2 bg-warning-soft0 rounded-full" />}
        </button>
        <button
          onClick={() => setActiveTab('security')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'security'
              ? 'border-red-600 text-danger'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <HiOutlineLockClosed className="w-4 h-4" />
          Document Security
          {secDirty && <span className="w-2 h-2 bg-warning-soft0 rounded-full" />}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'permissions' && (
        <PermissionMatrixTab
          matrix={permMatrix}
          meta={permMeta}
          loading={permLoading}
          saving={permSaving}
          dirty={permDirty}
          onToggle={togglePermission}
          onSave={savePermissions}
          onReset={resetPermissions}
        />
      )}

      {activeTab === 'security' && (
        <SecurityRulesTab
          matrix={secMatrix}
          meta={secMeta}
          loading={secLoading}
          saving={secSaving}
          dirty={secDirty}
          onToggle={toggleSecurityRule}
          onSave={saveSecurityRules}
          onReset={resetSecurityRules}
        />
      )}
    </div>
  )
}

// ==========================================
// Permission Matrix Tab
// ==========================================

const PermissionMatrixTab = ({ matrix, meta, loading, saving, dirty, onToggle, onSave, onReset }) => {
  if (loading || !matrix) {
    return <LoadingSkeleton />
  }

  const { roles, resources, actions } = meta

  return (
    <div>
      {/* Action Bar */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          Configure which actions each role can perform on each resource.
        </p>
        <div className="flex gap-2">
          <button
            onClick={onReset}
            disabled={!dirty || saving}
            className="px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
          >
            <HiOutlineArrowPath className="w-4 h-4" />
            Discard
          </button>
          <button
            onClick={onSave}
            disabled={!dirty || saving}
            className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
          >
            {saving ? (
              <>
                <HiOutlineArrowPath className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <HiOutlineCheckCircle className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {dirty && (
        <div className="mb-4 p-3 bg-warning-soft border border-warning/30 rounded-lg flex items-center gap-2 text-sm text-warning">
          <HiOutlineExclamationTriangle className="w-4 h-4 flex-shrink-0" />
          You have unsaved changes. Click "Save Changes" to apply.
        </div>
      )}

      {/* Scrollable Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              {/* Resource header row */}
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left font-semibold text-gray-700 border-r border-gray-200 min-w-[100px]">
                  Role
                </th>
                {resources.map(resource => {
                  const resourceActions = actions.filter(a => !HIDDEN_ACTIONS.includes(a))
                  return (
                    <th
                      key={resource}
                      colSpan={resourceActions.length}
                      className="px-2 py-3 text-center font-semibold text-gray-700 border-r border-gray-200 bg-gray-100"
                    >
                      {RESOURCE_LABELS[resource] || resource}
                    </th>
                  )
                })}
              </tr>
              {/* Action header row */}
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="sticky left-0 z-10 bg-gray-50 px-4 py-2 border-r border-gray-200" />
                {resources.map(resource => {
                  const resourceActions = actions.filter(a => !HIDDEN_ACTIONS.includes(a))
                  return resourceActions.map((action, idx) => (
                    <th
                      key={`${resource}-${action}`}
                      className={`px-1 py-2 text-center text-xs font-medium text-gray-500 ${
                        idx === resourceActions.length - 1 ? 'border-r border-gray-200' : ''
                      }`}
                    >
                      {ACTION_LABELS[action] || action}
                    </th>
                  ))
                })}
              </tr>
            </thead>
            <tbody>
              {roles.map((role, roleIdx) => (
                <tr
                  key={role}
                  className={`border-b border-gray-100 hover:bg-gray-50 ${roleIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
                >
                  <td className="sticky left-0 z-10 bg-inherit px-4 py-3 border-r border-gray-200">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border ${ROLE_COLORS[role]}`}>
                      {ROLE_LABELS[role] || role}
                    </span>
                  </td>
                  {resources.map(resource => {
                    const resourceActions = actions.filter(a => !HIDDEN_ACTIONS.includes(a))
                    return resourceActions.map((action, idx) => {
                      const isAllowed = matrix[role]?.[resource]?.[action] || false
                      return (
                        <td
                          key={`${resource}-${action}`}
                          className={`px-1 py-2 text-center ${
                            idx === resourceActions.length - 1 ? 'border-r border-gray-200' : ''
                          }`}
                        >
                          <label className="inline-flex items-center justify-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isAllowed}
                              onChange={() => onToggle(role, resource, action)}
                              className="w-4 h-4 text-danger border-gray-300 rounded focus:ring-red-500 cursor-pointer"
                            />
                          </label>
                        </td>
                      )
                    })
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ==========================================
// Security Rules Tab
// ==========================================

const SecurityRulesTab = ({ matrix, meta, loading, saving, dirty, onToggle, onSave, onReset }) => {
  if (loading || !matrix) {
    return <LoadingSkeleton />
  }

  const { roles, security_levels } = meta

  return (
    <div>
      {/* Action Bar */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          Control which roles can access documents at each security level.
        </p>
        <div className="flex gap-2">
          <button
            onClick={onReset}
            disabled={!dirty || saving}
            className="px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
          >
            <HiOutlineArrowPath className="w-4 h-4" />
            Discard
          </button>
          <button
            onClick={onSave}
            disabled={!dirty || saving}
            className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
          >
            {saving ? (
              <>
                <HiOutlineArrowPath className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <HiOutlineCheckCircle className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {dirty && (
        <div className="mb-4 p-3 bg-warning-soft border border-warning/30 rounded-lg flex items-center gap-2 text-sm text-warning">
          <HiOutlineExclamationTriangle className="w-4 h-4 flex-shrink-0" />
          You have unsaved changes. Click "Save Changes" to apply.
        </div>
      )}

      {/* Security Rules Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden max-w-2xl">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left font-semibold text-gray-700 border-r border-gray-200">
                Role
              </th>
              {security_levels.map(level => {
                const info = SECURITY_LEVEL_LABELS[level]
                return (
                  <th key={level} className="px-4 py-3 text-center border-r border-gray-200 last:border-r-0">
                    <div className={`inline-flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg ${info?.bg} ${info?.border} border`}>
                      <span className={`font-semibold ${info?.color}`}>{info?.label || level}</span>
                      <span className="text-xs text-gray-500 font-normal">{info?.subtitle}</span>
                    </div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {roles.map((role, roleIdx) => (
              <tr
                key={role}
                className={`border-b border-gray-100 hover:bg-gray-50 ${roleIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
              >
                <td className="px-4 py-3 border-r border-gray-200">
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border ${ROLE_COLORS[role]}`}>
                    {ROLE_LABELS[role] || role}
                  </span>
                </td>
                {security_levels.map(level => {
                  const isAllowed = matrix[level]?.[role] || false
                  return (
                    <td key={level} className="px-4 py-3 text-center border-r border-gray-200 last:border-r-0">
                      <label className="inline-flex items-center justify-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isAllowed}
                          onChange={() => onToggle(level, role)}
                          className="w-5 h-5 text-danger border-gray-300 rounded focus:ring-red-500 cursor-pointer"
                        />
                      </label>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ==========================================
// Loading Skeleton
// ==========================================

const LoadingSkeleton = () => (
  <div className="space-y-4">
    <div className="h-10 bg-gray-200 rounded animate-pulse w-1/3" />
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />
        ))}
      </div>
    </div>
  </div>
)

export default PermissionManagementPage
